<template>
  <Loader :value="promises.fetchOne" class="model-template-mutate view" :message="$metaInfo.title" :key="id">
    <div class="container" v-if="loaded">
      <HeaderComponent :title="$metaInfo.title" :breadcrumb="breadcrumb">
        <template #right>
          <div class="row wrap g-1">
            <Actions placement="modelTemplate" :query="{ modelTemplate: id }" />

            <ButtonComponent
              :label="saveState.label"
              :success-label="$t('saved')"
              :loading-label="$t('saving')"
              :icon="['fal', saveState.icon]"
              theme="primary"
              :promise="promises.mutate"
              :disabled="!validator.passes"
              :class="{
                'elder-button--disabled': saveState.disabled,
                'elder-button--invalid': saveState.invalid,
              }"
              v-tooltip="{ content: saveState.tooltip }"
              @click="submit"
            />
          </div>
        </template>
      </HeaderComponent>

      <StepperComponent :value="steps" ref="stepper" state-handler="route" @submit="submit">
        <template #after="{ meta, next, prev }">
          <div class="row center g-1">
            <ButtonComponent
              v-if="meta.prev"
              :label="$t('prev')"
              :icon="['fal', 'angle-left']"
              icon-placement="left"
              @click="onNavigation(prev)"
            />
            <ButtonComponent
              v-if="meta.next"
              :label="$t('next')"
              :icon="['fal', 'angle-right']"
              @click="onNavigation(next)"
            />

            <ButtonComponent
              v-if="!meta.next"
              :label="isNew ? $t('create') : $t('complete')"
              :success-label="$t('completed')"
              :loading-label="$t('saving')"
              :icon="isNew ? ['fal', 'plus'] : ['fal', 'check']"
              theme="primary"
              type="button"
              :disabled="!validator.passes"
              :class="{
                'elder-button--disabled': saveState.disabled,
              }"
              :promise="promises.mutate"
              @click="$ev => (isNew ? submit($ev) : complete($ev))"
            />
          </div>
        </template>
      </StepperComponent>
    </div>
  </Loader>
</template>

<script>
import ModelMixin from '@/mixins/Model'
import ValidatorMixin from '@/mixins/validator-v2'
import { getValidatorConfig } from '@kvass/custom-fields'
import { mapState, mapActions } from 'vuex'
import { mapi18n } from '@/i18n'

import { buildStepsFields } from '@/utils'

import { StepperComponent } from 'vue-elder-stepper'
import { DropdownComponent } from 'vue-elder-dropdown'
import HeaderComponent from '@/components/Header'
import Actions from '@/components/Actions'

import ModelTemplateSteps from '@/views/ModelTemplate/Mutate/Steps'
import modelTemplateFields from '@/components/ModelTemplate/StepsFields/fields.js'

import ProjectSteps from '@/views/Project/Mutate/Steps'
import projectFields from '@/components/Project/StepsFields/fields.js'

import PageSteps from '@/views/Page/Mutate/Steps'
import pageFields from '@/components/Page/StepsFields/fields.js'

import ModelTemplateModel from '@/models/ModelTemplate/base'
import ProjectModel from '@/models/Project/base'
import PageModel from '@/models/Page/base'

import { mergeDeepRight, props } from 'ramda'
import getMediaTypesFromTemplate from '@/utils/getMediaTypesFromTemplate'

export default {
  mixins: [
    ValidatorMixin({
      rules: 'rules',
      labels: 'labels',
      data: 'data',
    }),
    ModelMixin({
      source: 'reactiveItem',
      guard: true,
      model(...args) {
        const base = ModelTemplateModel.call(this, ...args)

        if (this.isNew || !this.$path('item.id')) return base

        const specific = this.config.model.call(this, ...args)

        return mergeDeepRight(base, specific)
      },
      transform: {
        'customFieldsConfig.*.schema': undefined,
      },
    }),
  ],
  props: {
    id: String,
  },
  data() {
    return {
      loaded: false,
    }
  },
  watch: {
    id: {
      handler(id) {
        return this.fetch({ id }).then(data => {
          this.loaded = true

          // reset data structure from type
          this.$store.commit(`${data.ofType}/set`, {})
        })
      },
      immediate: true,
    },
  },
  computed: {
    ...mapState('ModelTemplate', ['item', 'promises', 'states']),

    types() {
      const template = this.$path('data.theme.template', this.item) || this.$path('data.theme', this.item)
      return getMediaTypesFromTemplate(template)
    },

    labels() {
      return {
        categories: this.$tc('category', 2),
        categoriesPlaceholder: this.$tc('add'),
        ...mapi18n({
          'media.logo': 'logo',
          'media.logoInverted': 'logoInverted',
          favicon: 'favicon',
          description: 'description',
          gallery: 'gallery',
          bidContract: 'bidContract',

          cover: 'cover',
          brochure: 'brochure',
          attachments: ['attachment', 2],

          name: 'name',
          ofType: 'type',
          addAsDefault: 'addAsDefault',
          yes: 'yes',
          no: 'no',
          project: 'project',
          page: 'page',
          'data.theme': 'theme',
          'data.theme.template': 'theme',
        }),
        ...this.customFieldValidatorConfig.labels,
      }
    },
    rules() {
      return {
        name: 'required',
        ofType: 'required',
        'data.theme': 'required_if:ofType,Project',
        'data.theme.template': 'required_if:ofType,Page',
        ...this.customFieldValidatorConfig.rules,
      }
    },

    customFieldSchemas() {
      const base = (this.$path('item.customFieldsConfig') || [])
        .map(c => (c.schema || []).map(e => ({ ...e, placement: c.placement || e.placement })))
        .flat()

      const ofType = this.data && this.data.ofType
      if (ofType === 'Project' || ofType === 'Page') {
        base.unshift({
          key: ofType === 'Page' ? 'data-theme-template' : 'data-theme',
          component: 'Slot',
          placement: 'template',
          label: this.$t('selectTheme'),
          options: {
            collapsible: true,
            expanded: true,
            size: 2,
            props: {
              sublabel: this.$t(ofType === 'Page' ? 'pageThemeHelper' : 'projectThemeHelper'),
            },
          },
        })
      }

      return base
    },

    customFieldValidatorConfig() {
      if (!this.customFieldSchemas.length) return {}

      return getValidatorConfig(this.customFieldSchemas, this.data, { prefix: 'customFields' })
    },

    breadcrumb() {
      return [
        { title: this.$tc('template', 1), action: { name: 'settings', params: { step: 'template' } } },
        { title: this.$metaInfo.title },
      ]
    },

    saveState() {
      let base = {
        label: this.isNew ? this.$t('create') : this.$t('save'),
        icon: this.isNew ? 'plus' : 'arrow-to-bottom',
        disabled: !this.validator.passes || !this.hasChanges,
      }

      if (!this.hasChanges) {
        base.tooltip = this.$t('noUnsavedChanges')
      }

      if (!this.validator.passes) {
        const invalid = {
          invalid: true,
          icon: 'exclamation-circle',
          tooltip: Object.values(this.validator.errors.all()).join('<br>'),
        }

        Object.assign(base, invalid)
      }

      return base
    },

    isNew() {
      return !this.id
    },

    config() {
      switch (this.$path('item.ofType')) {
        case 'Page':
          return {
            model: PageModel,
            validSteps: this.isNew ? [] : ['theme', 'presentation', 'devtools'],
            steps: PageSteps,
            fields: pageFields,
          }

        case 'Project':
        default:
          return {
            model: ProjectModel,
            validSteps: this.isNew
              ? []
              : ['theme', 'designtilpasninger', 'presentation', 'sale', 'publishing', 'devtools'],
            steps: ProjectSteps,
            fields: projectFields,
          }
      }
    },

    reactiveItem() {
      if (!this.item || !this.item.data) return this.item
      return mergeDeepRight(this.item, this.item.data)
    },

    steps() {
      if (!this.loaded) return []

      const data = mergeDeepRight(this.reactiveItem, { ...this.data, userRoles: this.$store.getters })

      const ctx = {
        data,
        labels: this.labels,
        types: this.types,
        $t: this.$t.bind(this),
        isNew: this.isNew,
      }

      let props = {
        value: this.data,
        data: data,
        labels: this.labels,
        validator: this.validator,
        promises: this.promises,
        promises: this.promises,
        customFieldSchemas: this.customFieldSchemas,
        fields: buildStepsFields(
          [...Array.from(this.config.fields.values()), ...Array.from(modelTemplateFields.values())],
          this.customFieldSchemas,
          ctx,
        ),
      }

      let listeners = {
        submit: () => this.submit(),
      }

      const steps = [
        ...this.config.steps(data).filter(raw => {
          return this.config.validSteps.some(step => step.includes(raw.id))
        }),
        ...ModelTemplateSteps(data),
      ]
        .sort((a, b) => {
          return a.order - b.order
        })
        .map(raw => {
          const { icon, ...s } = raw
          s.props = {
            ...(s.props || {}),
            ...props,
            fields: {
              ...props.fields,
              default: props.fields.default.filter(i => i.defaultPlacement === raw.id),
              slots: props.fields.slots.filter(i => !i.defaultPlacement || i.defaultPlacement === raw.id),
            },
          }
          s.listeners = {
            ...(s.listeners || {}),
            ...listeners,
          }
          return s
        })

      return steps
    },
  },
  methods: {
    ...mapActions('ModelTemplate', ['fetch', 'mutate', 'remove']),

    submit() {
      if (!this.validator.passes || !this.hasChanges) return Promise.resolve()

      const splitData = flatData => {
        const { customFields, name, isDefault, categories, ofType, ...rest } = flatData
        return {
          name,
          isDefault,
          categories,
          customFields,
          ofType,
          data: rest,
        }
      }

      const getPayload = () => {
        if (this.isNew) return this.getData({ diff: false })

        return splitData(this.getData({ diff: true }))
      }

      return this.mutate([
        {
          ...getPayload(),
          id: this.id,
        },
        {
          strategy: (item, payload) => {
            const { id } = payload

            const fullFormData = this.getData({ diff: false })

            const structuredData = splitData(fullFormData)

            return mergeDeepRight(item, { ...structuredData, id })
          },
          transformPayload: true,
          draft: false,
        },
      ]).then(data => {
        if (!this.isNew) return this.fetch({ id: this.id })

        this.loaded = false
        return this.$router.push({ name: 'model-template-edit', params: { id: data.id } })
      })
    },
    complete() {
      this.submit().then(() => this.$router.push({ name: 'settings', params: { step: 'template' } }))
    },

    onNavigation(callback) {
      callback()
      this.submit()
    },
  },

  metaInfo() {
    return {
      title: this.isNew
        ? this.$t('createResource', {
            resource: this.$tc('template').toLowerCase(),
          })
        : this.item.name || this.$tc('loading') + '...',
    }
  },
  components: {
    HeaderComponent,
    StepperComponent,
    DropdownComponent,
    Actions,
  },
}
</script>

<style lang="scss">
.model-template-mutate {
  background-color: $background-grey;

  &[step='presentation'] {
    [data-key='flatfinder'] {
      display: none;
    }
  }

  [data-field='data'] {
    grid-column: 1 / -1;

    .card {
      max-width: unset;
      box-shadow: none;
      --card-spacing: 0;
    }
  }

  &[step='theme'] {
    .template-section-intro span,
    .page-mutate-theme__items,
    .page-mutate-theme__load-trigger,
    .project-mutate-theme__load-trigger,
    .project-mutate-theme__items {
      display: none;
    }
  }

  .elder-stepper__component > * {
    margin: 2rem auto;
  }
}
</style>
